import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LOCALES, STORAGE_KEY, swapLocale } from "../i18n/locales";
import StableLabel from "./StableLabel";

const LanguageSwitch = ({ onSwitch }) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const listId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const [open, setOpen] = useState(false);
  const current = LOCALES.find((locale) => locale.code === i18n.resolvedLanguage) || LOCALES[0];

  useEffect(() => {
    if (!open) return undefined;
    const selected = LOCALES.findIndex((locale) => locale.code === current.code);
    optionRefs.current[selected]?.focus();

    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, current.code]);

  const select = (code) => {
    setOpen(false);
    if (code === i18n.resolvedLanguage) {
      triggerRef.current?.focus();
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // The URL still carries the choice.
    }
    const htmlLang = LOCALES.find((locale) => locale.code === code)?.htmlLang;
    if (htmlLang) document.documentElement.lang = htmlLang;
    i18n.changeLanguage(code);
    navigate(`${swapLocale(location.pathname, code)}${location.search}${location.hash}`);
    onSwitch?.();
  };

  const onOptionKey = (event) => {
    const index = optionRefs.current.findIndex((node) => node === document.activeElement);
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const delta = event.key === "ArrowDown" ? 1 : -1;
    const next = (index + delta + LOCALES.length) % LOCALES.length;
    optionRefs.current[next]?.focus();
  };

  return (
    <div className={open ? "lang-switch is-open" : "lang-switch"} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="lang-current"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${t("nav.language")}: ${current.label}`}
        lang={current.htmlLang}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" || open) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <StableLabel alternatives={LOCALES.map((locale) => locale.label)}>{current.label}</StableLabel>
        <svg className="lang-chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      {open && (
        <ul id={listId} className="lang-menu" role="listbox" aria-label={t("nav.language")} onKeyDown={onOptionKey}>
          {LOCALES.map((locale, index) => (
            <li key={locale.code} role="presentation">
              <button
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                lang={locale.htmlLang}
                aria-selected={locale.code === current.code}
                onClick={() => select(locale.code)}
              >
                {locale.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitch;
